// SearchStateContext — Phase 1 of the URL Parameter Removal initiative
// (see reports/URL_PARAMETER_REMOVAL_PLAN.md).
//
// Provides a single source of truth for search/filter/pagination state so that, once the
// initial deep-link URL params are read on mount (Phase 2 — results.jsx, Phase 3 — search.jsx),
// the rest of the app can operate entirely off this context instead of the query string.
// State is mirrored to sessionStorage so it survives refreshes and browser back/forward
// (Phase 5) without ever needing to live in the URL.
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SearchStateContext = createContext();

export const useSearchState = () => {
  const context = useContext(SearchStateContext);
  if (!context) {
    throw new Error('useSearchState must be used within a SearchStateProvider');
  }
  return context;
};

// sessionStorage keys are kept as named constants (rather than inline strings) since later
// phases of this plan reference the same keys directly — see Phase 2.2 (initial read) in
// reports/URL_PARAMETER_REMOVAL_PLAN.md.
const SEARCH_STATE_KEY = 'searchState';
// Legacy key from an earlier Phase 5 design (a sessionStorage-backed push/pop history stack)
// that was superseded before shipping — see the Phase 5 implementation notes in the plan doc
// for why. Kept here ONLY so `clearSearchState()` still cleans up any leftover value a
// returning user's browser might have from a prior session; nothing writes to this key anymore.
const SEARCH_HISTORY_KEY = 'searchHistory';

// Mirrors the filters currently managed locally in results.jsx's `filters` state (including
// `handicapAccess`, which the plan document's original draft omitted), plus `sort` — which is
// folded into this same object going forward instead of living in a separate `sortOption`
// state as it does today.
const DEFAULT_FILTERS = {
  gender: 'no-preference',
  newPatientStatus: 'no-preference',
  distance: '25',
  visitType: 'no-preference',
  sort: 'Distance',
  language: [],
  specialty: [],
  boardCertified: 'no-preference',
  acceptsMedicaid: [],
  afterHoursWeekend: [],
  groupAffiliation: [],
  handicapAccess: [],
  providerRace: [],
  providerEthnicity: [],
  birthingFriendlyCare: 'no-preference',
  agesServed: 'no-preference',
  cshcs: [],
  pubTrans: [],
  hospitalAffiliation: [],
  specializedTraining: [],
  accreditation: [],
  specialProviderType: []
};

const DEFAULT_STATE = {
  // Search parameters
  query: '',
  superCategory: '',
  subCategory: '',
  popularSearchId: '',
  psLabel: '',

  // Location (deep-link only — day-to-day location detection lives in LocationContext)
  latitude: null,
  longitude: null,

  // The user's specific selected plan / line of business (e.g. "FMMA"), sourced from the
  // `planCode` query param on deep links or the plan dropdown on the search page. This is
  // NOT the environment code (e.g. "FL") — that comes only from the `:planCode` route path
  // segment (see Phase 0) and is intentionally never duplicated into this context.
  selectedPlan: '',

  // Filters (sort included — see DEFAULT_FILTERS above)
  filters: DEFAULT_FILTERS,

  // Pagination
  currentPage: 1,
  resultsPerPage: 5
};

// Defensive JSON parse — sessionStorage content could be missing, cleared mid-session, or
// (rarely) corrupted. Never let a parse failure crash the app; fall back gracefully instead.
function safeParse(raw, fallback) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch (e) {
    console.warn('⚠️ [SearchStateContext] Failed to parse stored value, using fallback:', e);
    return fallback;
  }
}

function readStoredSearchState() {
  try {
    return safeParse(sessionStorage.getItem(SEARCH_STATE_KEY), DEFAULT_STATE);
  } catch (e) {
    // sessionStorage itself can throw in some locked-down browser contexts (e.g. certain
    // privacy modes / sandboxed iframes) — fall back to in-memory defaults rather than crash.
    console.warn('⚠️ [SearchStateContext] sessionStorage unavailable, using in-memory default state:', e);
    return DEFAULT_STATE;
  }
}

export const SearchStateProvider = ({ children }) => {
  const [searchState, setSearchState] = useState(readStoredSearchState);

  // Persist to sessionStorage on every change so refresh / back-forward can restore it.
  useEffect(() => {
    try {
      sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(searchState));
    } catch (e) {
      console.warn('⚠️ [SearchStateContext] Failed to persist state to sessionStorage:', e);
    }
  }, [searchState]);

  // Merge-update the entire search state (search term, location, plan, filters, paging).
  const updateSearchState = useCallback((updates) => {
    setSearchState(prev => ({ ...prev, ...updates }));
  }, []);

  // Merge-update just the filters sub-object. Also resets to page 1, matching today's
  // results.jsx `handleFilterChange` behavior — a filter change always invalidates the
  // current page of results.
  const updateFilters = useCallback((filterUpdates) => {
    setSearchState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...filterUpdates },
      currentPage: 1
    }));
  }, []);

  // Apply several filter changes at once (e.g. sidebar "Apply" button) in a single update.
  const batchUpdateFilters = useCallback((filterUpdates) => {
    setSearchState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...filterUpdates },
      currentPage: 1
    }));
  }, []);

  // Full reset — used for "New Search" / navigating to a brand-new search from scratch.
  const clearSearchState = useCallback(() => {
    setSearchState(DEFAULT_STATE);
    try {
      sessionStorage.removeItem(SEARCH_STATE_KEY);
      sessionStorage.removeItem(SEARCH_HISTORY_KEY);
    } catch (e) {
      console.warn('⚠️ [SearchStateContext] Failed to clear sessionStorage:', e);
    }
  }, []);

  // Reset only the filters back to defaults (keep the current search term/location/plan).
  const resetFilters = useCallback(() => {
    setSearchState(prev => ({
      ...prev,
      filters: DEFAULT_FILTERS,
      currentPage: 1
    }));
  }, []);

  // --- Phase 5 (Browser Navigation Support) design note ---
  // This context originally exposed a `pushToHistory`/`popFromHistory` pair here: a
  // sessionStorage-backed array, manually `.push()`/`.pop()`'d by consumers, intended to back
  // the browser back button. It was removed before any consumer used it, during Phase 5's own
  // implementation, for a fundamental reason: a LIFO pop *permanently discards* the entry it
  // returns, so it cannot support the forward button at all (there'd be nothing left to redo)
  // — and Phase 5's own checklist/test plan explicitly requires forward-button support.
  // Extending it into a correct two-stack (back/forward) design, PLUS reliably detecting which
  // direction a raw `popstate` event moved (including multi-step jumps), would have meant
  // reimplementing what the browser's native session history already does for free. Phase 5
  // instead attaches each "new search" action's complete resulting state directly to its
  // `navigate(path, { state })` call (see search.jsx / results.jsx) — the browser then tracks
  // back/forward (correctly, bidirectionally, multi-step-safe) on its own, and consumers just
  // read `useLocation().state` back on the other end. See the Phase 5 implementation notes in
  // reports/URL_PARAMETER_REMOVAL_PLAN.md for the full reasoning.

  const value = {
    searchState,
    updateSearchState,
    updateFilters,
    batchUpdateFilters,
    clearSearchState,
    resetFilters
  };

  return (
    <SearchStateContext.Provider value={value}>
      {children}
    </SearchStateContext.Provider>
  );
};

// Exported for reuse by later phases (e.g. equality checks against "is this the default
// state", or resetting without duplicating the object literal) without reaching into
// context internals.
export { DEFAULT_STATE, DEFAULT_FILTERS };

export default SearchStateContext;
